/**
 * プリセット作成/編集ページ コンテナ
 */
import type React from "react";
import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  type CreatePresetInput,
  useCreatePreset,
  useParsePresetText,
  usePreset,
  useUpdatePreset,
} from "../../../hooks/use-preset-api";
import { PresetFormPage } from "./PresetFormPage";

/**
 * プリセット作成/編集ページのコンテナコンポーネント
 * @return {JSX.Element} PresetFormPage コンテナ
 */
const PresetFormPageContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const { preset, loading } = usePreset(id);
  const { createPreset, loading: isCreating } = useCreatePreset();
  const { updatePreset, loading: isUpdating } = useUpdatePreset();
  const { parseText, loading: isParsing } = useParsePresetText();

  const handleSubmit = useCallback(
    async (input: CreatePresetInput) => {
      if (isEdit && id) {
        await updatePreset(id, input);
      } else {
        await createPreset(input);
      }
      navigate("/settings/presets");
    },
    [isEdit, id, createPreset, updatePreset, navigate],
  );

  const handleParseText = useCallback(
    async (text: string, presetType: string) => {
      return await parseText(text, presetType);
    },
    [parseText],
  );

  return (
    <PresetFormPage
      isEdit={isEdit}
      initialData={preset}
      loading={isEdit && loading}
      onSubmit={handleSubmit}
      onParseText={handleParseText}
      isSubmitting={isCreating || isUpdating}
      isParsing={isParsing}
    />
  );
};

export default PresetFormPageContainer;
